import { Component, DestroyRef, EventEmitter, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaptchaService } from '../../services/captcha.service';
import { Captcha, CaptchaActions } from '../models/captcha.interface';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EUI_SELECT } from '@eui/components/eui-select';
import { EUI_INPUT_CHECKBOX } from '@eui/components/eui-input-checkbox';
import { EUI_INPUT_TEXT } from '@eui/components/eui-input-text';
import { EUI_BUTTON } from '@eui/components/eui-button';
import { EUI_FEEDBACK_MESSAGE } from '@eui/components/eui-feedback-message';

@Component({
    selector: 'app-textual-captcha',
    templateUrl: './textual.captcha.component.html',
    styleUrls: ['./textual.captcha.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
        ...EUI_SELECT,
        ...EUI_INPUT_CHECKBOX,
        ...EUI_INPUT_TEXT,
        ...EUI_BUTTON,
        ...EUI_FEEDBACK_MESSAGE,
    ],
})
export class TextualCaptchaComponent implements OnInit, CaptchaActions {
    private captchaService = inject(CaptchaService);
    private translateService = inject(TranslateService);
    public fb = inject(FormBuilder);
    private destroyRef = inject(DestroyRef);

    useAudio = false;

    lang = 'en';

    /* Captcha Information */
    captchaImage = signal<string | undefined>(undefined);
    captchaId = '';
    audioCaptcha = '';
    degree: number | undefined;
    /* end Captcha Information */

    answer = '';

    validationStatus = false;

    showAlert = signal(false);
    alertMessage = '';

    @Output() validationChange = new EventEmitter<boolean>();

    captchaForm: FormGroup;

    constructor() {
        this.captchaForm = this.fb.group({
            language: [''],
            answer: [''],
        });
    }

    captchaLang = [
        { id: 'en', name: 'English' },
        { id: 'fr', name: 'Français' },
        { id: 'de', name: 'Deutsch' },
        { id: 'bg', name: 'български' },
        { id: 'hr', name: 'Hrvatski' },
        { id: 'da', name: 'Dansk' },
        { id: 'es', name: 'Español' },
        { id: 'et', name: 'Eesti keel' },
        { id: 'fi', name: 'Suomi' },
        { id: 'el', name: 'ελληνικά' },
        { id: 'hu', name: 'Magyar' },
        { id: 'it', name: 'Italiano' },
        { id: 'lv', name: 'Latviešu valoda' },
        { id: 'lt', name: 'Lietuvių kalba' },
        { id: 'mt', name: 'Malti' },
        { id: 'nl', name: 'Nederlands' },
        { id: 'pl', name: 'Polski' },
        { id: 'pt', name: 'Português' },
        { id: 'ro', name: 'Română' },
        { id: 'sk', name: 'Slovenčina' },
        { id: 'sl', name: 'Slovenščina' },
        { id: 'sv', name: 'Svenska' },
        { id: 'cs', name: 'čeština' },
    ];

    ngOnInit(): void {
        this.captchaForm
            .get('answer')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                this.answer = value ?? '';
            });
        this.setInitialLanguage();
        this.captchaService
            .getCaptcha('STANDARD')
            .subscribe((captchaRequest: HttpResponse<Captcha>) => {
                const captcha: Captcha = captchaRequest.body as Captcha;

                this.captchaImage.set(`data:image/png;base64,${captcha.captchaImg}`);
                this.captchaId = captcha.captchaId;

                this.audioCaptcha = `data:audio/wav;base64,${captcha.audioCaptcha}`;

                console.log(this.audioCaptcha);

                this.degree = captcha.degree;
            });
    }

    private setInitialLanguage(): void {
        const currentLang = (this.translateService.currentLang || this.lang)
            .toLowerCase()
            .split('-')[0];
        const matchingLanguage = this.captchaLang.find((language) => language.id === currentLang);

        this.lang = matchingLanguage?.id ?? this.lang;
        this.captchaService.captchaLanguage = this.lang;
        this.captchaForm.patchValue({ language: this.lang }, { emitEvent: false });
    }

    onValidate = (onSuccess?: () => void, onFailure?: (error) => void) => {
        this.captchaService
            .validateCaptcha(this.captchaId, this.answer, this.useAudio, 'STANDARD')
            .subscribe(
                (res: { responseCaptcha?: string }) => {
                    const { responseCaptcha = 'fail' } = res;
                    this.validationStatus = responseCaptcha != 'fail';
                    this.validationChange.emit(this.validationStatus);
                    if (this.validationStatus) {
                        // this.alertClass = "alert-success";
                        this.onRefresh();
                        onSuccess?.();
                    } else {
                        this.onRefresh();
                        onFailure?.(res);
                        this.alertMessage = this.translateService.instant(
                            'page.support.captcha.invalid'
                        );
                        this.showAlert.set(true);
                    }

                    this.validationStatusExpiration();
                },
                (error) => {
                    onFailure?.(error);
                    this.validationChange.emit(false);
                    this.onRefresh();
                }
            );
    };
    onRefresh = () => {
        this.useAudio = false;
        this.validationStatus = false;
        this.validationChange.emit(false);
        this.answer = '';
        this.captchaForm.get('answer')?.reset('', { emitEvent: false });
        console.log(this.captchaForm.value);
        this.lang = this.captchaForm.value.language;
        this.captchaService
            .reloadCaptcha(this.captchaId, 'STANDARD', this.lang)
            .subscribe((captchaRequest) => {
                const captcha: Captcha = captchaRequest.body as Captcha;
                this.captchaImage.set(`data:image/png;base64,${captcha.captchaImg}`);
                this.captchaId = captcha.captchaId;
                this.audioCaptcha = `data:audio/wav;base64,${captcha.audioCaptcha}`;
                this.degree = captcha.degree;
            });
    };

    validationStatusExpiration = () => {
        setTimeout(() => {
            this.showAlert.set(false);
            console.log(`validation status become  ${this.validationStatus}`);
        }, 2000);
    };

    onPlayAudio() {
        this.useAudio = true;
    }
}
